import { initClient, type InitClientReturn } from '@ts-rest/core';
import { contract } from '@evogard/contract';

const clientArgs = {
  baseUrl: '',
  tenantHeaders: {},
  credentials: 'same-origin',
} as const;

/**
 * Cliente tipado da API, gerado do mesmo contrato que o backend implementa.
 * Rota que muda de forma quebra o build das duas SPAs — que é o ponto.
 *
 * `baseUrl` vazio: as duas SPAs são servidas do mesmo host que a API, e o
 * cookie de sessão vai junto com `same-origin`.
 *
 * O tipo é declarado, e não inferido: com o contrato inteiro, o inferido passou
 * do tamanho que o `tsc` aceita serializar no `.d.ts` (TS7056, em 20/10/2026).
 * O tipo é o mesmo que o `initClient` devolve — nomeá-lo não afrouxa nada.
 */
export const api: InitClientReturn<typeof contract, typeof clientArgs> = initClient(
  contract,
  clientArgs,
);

/** Erro da API no formato do contrato, para exibir uma mensagem de verdade. */
export function errorMessage(body: unknown, pattern = 'Algo deu errado.'): string {
  if (
    typeof body === 'object' &&
    body !== null &&
    'message' in body &&
    typeof (body as { message: unknown }).message === 'string'
  ) {
    return (body as { message: string }).message;
  }
  return pattern;
}

interface Response {
  status: number;
  body: unknown;
}

/**
 * A resposta que a tela esperava, ou o erro que ela vai mostrar.
 *
 * O ts-rest devolve uma união por status; a tela só quer o corpo do status de
 * sucesso e uma mensagem para o resto. `ok(r)` faz as duas coisas e estreita o
 * tipo: o `body` que sai é o do status pedido.
 *
 * A mensagem é a do servidor quando ele mandou uma (`ApiError.message`, em
 * pt-BR, escrita para a pessoa), e `fallback` quando não mandou — uma
 * resposta sem corpo, um proxy no meio. Antes deste helper metade das telas
 * mostrava só o literal e engolia a razão que a API tinha dado.
 *
 * Quem precisa tratar um status específico (um 404 que vira "não encontrado"
 * na tela, um 429 que vira "espere um pouco") continua lendo `r.status` à mão:
 * isto é para o caso comum, não para todos.
 */
export function ok<R extends Response, S extends R['status'] = 200>(
  r: R,
  status?: S,
  fallback?: string,
): Extract<R, { status: S }>['body'] {
  if (r.status !== (status ?? 200)) throw new Error(errorMessage(r.body, fallback));
  return r.body as Extract<R, { status: S }>['body'];
}

/**
 * A requisição não chegou a receber resposta.
 *
 * Existe porque "sem sinal" e "a Evogard respondeu errado" levam a pessoa a
 * fazer coisas opostas — sair do elevador ou ligar para a operação — e o
 * navegador só distingue os dois de uma forma: o `fetch` **rejeita** quando
 * não houve resposta, e resolve quando houve, mesmo que com 500.
 *
 * `navigator.onLine` não serve para isso sozinho. Ele mente numa direção:
 * `false` é confiável, `true` só quer dizer que existe uma interface de rede —
 * o wi-fi do hotel que pede login devolve `true` e não entrega nada.
 *
 * Nasceu no portal, que é quem roda no telefone; mora aqui porque a interna
 * cai no mesmo caso na gaveta do telefone.
 */
export class Unreachable extends Error {
  constructor(cause: unknown) {
    super('Não foi possível falar com a Evogard.', { cause });
    this.name = 'Unreachable';
  }
}

/**
 * Executa uma chamada da API traduzindo a falha de rede em `Unreachable`.
 *
 * Tudo o que a API responde — inclusive erro — passa direto: só a ausência de
 * resposta vira este erro.
 */
export async function withReachability<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } catch (cause) {
    throw new Unreachable(cause);
  }
}
